import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import projectsService, { type Project, type ProjectCreate, type ProjectUpdate } from '../services/projects';
import { useAuth } from '../context/AuthContext';
import AzureAddressAutocomplete from '../components/AzureAddressAutocomplete';
import { TIMEZONES, type TimezoneOption } from '../data/timezones';

const ProjectEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNewProject = !projectId || projectId === 'new';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    project_name: '',
    company_id: user?.CompanyID || '',
    timezone: '',
    client: '',
    unit: '',
    address: '',
    latitude: '',
    longitude: ''
  });

  // Existing client names for the autocomplete; free typing is still allowed
  // so a first project can be created for a brand-new client.
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  useEffect(() => {
    projectsService.getClients().then(setClientOptions).catch(() => {});
  }, []);

  const UNIT_PREFIX_REGEX =
    /^\s*((?:unit|suite|shop|apt\.?|apartment|level|lvl|lot)\s+[\w-]+|\d+\s*\/(?=\s*\d))\s*[,\s]+/i;

  const splitUnitFromAddress = (full: string): { unit: string; address: string } => {
    if (!full) return { unit: '', address: '' };
    const match = full.match(UNIT_PREFIX_REGEX);
    if (!match) return { unit: '', address: full };
    return {
      unit: match[1].trim(),
      address: full.slice(match[0].length).trim(),
    };
  };

  const combineUnitAndAddress = (unit: string, address: string): string => {
    const u = unit.trim();
    const a = address.trim();
    if (!u) return a;
    if (!a) return u;
    return `${u}, ${a}`;
  };

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const project = await projectsService.getProject(projectId);
      
      if (!project) {
        setError('Project data not found');
        return;
      }
      
      // API responses are inconsistent (Pascal vs snake_case across endpoints)
      const p = project as any;
      const pick = (...keys: string[]): string => {
        for (const k of keys) {
          const v = p[k];
          if (v !== undefined && v !== null && v !== '') return String(v);
        }
        return '';
      };
      const addressRaw = pick('address', 'Address');
      const newFormData = {
        project_name: pick('project_name', 'ProjectName', 'projectName'),
        company_id: pick('company_id', 'Companyid', 'CompanyId', 'companyId'),
        timezone: pick('timezone', 'TimeZone', 'time_zone', 'TimeZoneId'),
        client: pick('client', 'Client'),
        ...splitUnitFromAddress(addressRaw),
        latitude: pick('latitude', 'Latitude'),
        longitude: pick('longitude', 'Longitude'),
      };

      setFormData(newFormData);
      
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('Project not found');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to fetch project');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!isNewProject && projectId) {
      fetchProject();
    } else {
      setLoading(false);
    }
  }, [projectId, isNewProject, fetchProject]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      if (isNewProject) {
        // Create new project
        const createData: ProjectCreate = {
          project_name: formData.project_name,
          company_id: formData.company_id,
          timezone: formData.timezone || undefined,
          client: formData.client || undefined,
          address: combineUnitAndAddress(formData.unit, formData.address) || undefined,
          latitude: formData.latitude || undefined,
          longitude: formData.longitude || undefined,
        };
        
        await projectsService.createProject(createData);
        setSuccess(t('projectEdit.createSuccess'));
        
        // Navigate back to projects list after a short delay
        setTimeout(() => {
          navigate('/projects');
        }, 1000);
        
      } else {
        // Update existing project
        const updateData: ProjectUpdate = {
          project_name: formData.project_name,
          timezone: formData.timezone || undefined,
          client: formData.client || undefined,
          address: combineUnitAndAddress(formData.unit, formData.address) || undefined,
          latitude: formData.latitude || undefined,
          longitude: formData.longitude || undefined,
        };
        
        await projectsService.updateProject(projectId!, updateData);
        setSuccess(t('projectEdit.updateSuccess'));
      }
      
    } catch (err: any) {
      if (err?.response?.status === 400) {
        setError(err?.response?.data?.detail || 'Invalid project data');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to save project');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleBack = () => {
    navigate('/projects');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label={t('projectEdit.projectName')}
              value={formData.project_name}
              onChange={handleChange('project_name')}
              required
              fullWidth
            />

            <Autocomplete
              freeSolo
              fullWidth
              options={clientOptions}
              value={formData.client || null}
              inputValue={formData.client}
              onInputChange={(_, value) => {
                setFormData(prev => ({ ...prev, client: value }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('projectEdit.client')}
                  placeholder={t('projectEdit.clientPlaceholder', 'Select or type a client')}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Unit / Level"
                value={formData.unit}
                onChange={handleChange('unit')}
                placeholder="e.g. Unit 7"
                sx={{ flex: '0 0 180px' }}
              />
              <Box sx={{ flex: 1 }}>
                <AzureAddressAutocomplete
                  label={t('projectEdit.address')}
                  value={formData.address}
                  placeholder="e.g. 123 Wallaby Street, Sydney NSW 2000"
                  onChange={(address, lat, lon) => {
                    setFormData(prev => ({
                      ...prev,
                      address,
                      ...(lat && lon ? { latitude: lat, longitude: lon } : {}),
                    }));
                  }}
                />
              </Box>
            </Box>

            <Autocomplete
              fullWidth
              options={TIMEZONES}
              getOptionLabel={(option: TimezoneOption) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              value={TIMEZONES.find(tz => tz.value === formData.timezone) || null}
              onChange={(_, selected) => {
                setFormData(prev => ({ ...prev, timezone: selected?.value || '' }));
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('projectEdit.timezone')} />
              )}
            />

            <Divider />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || !formData.project_name.trim()}
              >
                {saving ? <CircularProgress size={20} /> : isNewProject ? t('projectEdit.createProject') : t('projectEdit.updateProject')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectEdit;